import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { THEMES, DEFAULT_THEME_ID, THEME_STORAGE_KEY } from "./themes";

const ThemeContext = createContext(null);

const applyThemeToRoot = (theme) => {
  const r = document.documentElement;
  const c = theme.colors;
  r.setAttribute("data-theme", theme.id);
  r.style.setProperty("--bg", c.background);
  r.style.setProperty("--surface", c.surface);
  r.style.setProperty("--surface2", c.surface2);
  r.style.setProperty("--border", c.border);
  r.style.setProperty("--fg", c.textPrimary);
  r.style.setProperty("--fg-2", c.textSecondary);
  r.style.setProperty("--fg-3", c.textMuted);
  r.style.setProperty("--green", c.greenHero);
  r.style.setProperty("--green-soft", c.greenSoft);
  r.style.setProperty("--green-glow", c.greenGlow);
  r.style.setProperty("--loss", c.lossHero);
  r.style.setProperty("--accent", c.accent);
  r.style.setProperty("--accent-soft", c.accentSoft || c.greenSoft);
  r.style.setProperty("--accent-glow", c.accentGlow || c.greenGlow);
  r.style.setProperty("--accent-gradient", c.accentGradient || `linear-gradient(135deg, ${c.accent} 0%, ${c.accent} 100%)`);
  r.style.setProperty("--radius", theme.radius);
  r.style.setProperty("--font-heading", theme.fonts.heading);
  r.style.setProperty("--font-body", theme.fonts.body);
  r.style.setProperty("--font-mono", theme.fonts.mono);
  // Apply base bg + fg to <body> so the whole app reflects theme immediately
  document.body.style.backgroundColor = c.background;
  document.body.style.color = c.textPrimary;
  document.body.style.fontFamily = theme.fonts.body;
};

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_THEME_ID;
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored && THEMES[stored] ? stored : DEFAULT_THEME_ID;
  });

  const theme = THEMES[themeId] || THEMES[DEFAULT_THEME_ID];

  useEffect(() => {
    applyThemeToRoot(theme);
    try { window.localStorage.setItem(THEME_STORAGE_KEY, theme.id); } catch { /* ignore quota */ }
  }, [theme]);

  const setTheme = useCallback((id) => {
    if (THEMES[id]) setThemeId(id);
  }, []);

  const value = useMemo(() => ({ theme, setTheme, themes: Object.values(THEMES) }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
};
